// Generat automat din README.md - Bulk Operations System
import React, { useState } from 'react';
import { CheckSquare, Square, Trash2, Edit, Download, Archive, MoreHorizontal, X } from 'lucide-react';

export const useBulkSelection = (data) => {
  const [selectedItems, setSelectedItems] = useState(new Set());

  const toggleItem = (itemId) => {
    const newSelected = new Set(selectedItems);
    if (newSelected.has(itemId)) {
      newSelected.delete(itemId);
    } else {
      newSelected.add(itemId);
    }
    setSelectedItems(newSelected);
  };

  const toggleAll = () => {
    if (selectedItems.size === data.length) {
      setSelectedItems(new Set());
    } else {
      setSelectedItems(new Set(data.map(item => item.id)));
    }
  };

  const clearSelection = () => {
    setSelectedItems(new Set());
  };

  const selectRange = (startId, endId) => {
    const startIndex = data.findIndex(item => item.id === startId);
    const endIndex = data.findIndex(item => item.id === endId);
    
    if (startIndex === -1 || endIndex === -1) return;
    
    const rangeStart = Math.min(startIndex, endIndex);
    const rangeEnd = Math.max(startIndex, endIndex);
    
    const newSelected = new Set(selectedItems);
    for (let i = rangeStart; i <= rangeEnd; i++) {
      newSelected.add(data[i].id);
    }
    setSelectedItems(newSelected);
  };

  const getSelectedData = () => {
    return data.filter(item => selectedItems.has(item.id));
  };

  const isAllSelected = selectedItems.size === data.length && data.length > 0;
  const isPartiallySelected = selectedItems.size > 0 && selectedItems.size < data.length;

  return {
    selectedItems,
    setSelectedItems,
    toggleItem,
    toggleAll,
    clearSelection,
    selectRange,
    getSelectedData,
    isAllSelected,
    isPartiallySelected,
    selectedCount: selectedItems.size
  };
};

// Bulk Actions Toolbar
const BulkActionsToolbar = ({
  selectedCount,
  onBulkDelete,
  onBulkEdit,
  onBulkExport,
  onBulkArchive,
  customActions = [],
  onClearSelection
}) => {
  const [showDropdown, setShowDropdown] = useState(false);

  if (selectedCount === 0) return null;

  const defaultActions = [
    {
      key: 'delete',
      label: 'Șterge Selectate',
      icon: Trash2,
      onClick: onBulkDelete,
      variant: 'danger',
      requiresConfirmation: true
    },
    {
      key: 'export',
      label: 'Exportă Selectate',
      icon: Download,
      onClick: onBulkExport,
      variant: 'primary'
    },
    {
      key: 'archive',
      label: 'Arhivează',
      icon: Archive,
      onClick: onBulkArchive,
      variant: 'secondary'
    }
  ];

  const allActions = [...defaultActions, ...customActions];

  return (
    <div className="fixed bottom-4 left-1/2 transform -translate-x-1/2 z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 p-4 flex items-center space-x-4 min-w-max">
        {/* Selection Info */}
        <div className="flex items-center space-x-2">
          <CheckSquare className="w-5 h-5 text-primary-600" />
          <span className="text-sm font-medium text-gray-900 dark:text-white">
            {selectedCount} selectate
          </span>
        </div>

        {/* Quick Actions */}
        <div className="flex items-center space-x-2">
          {allActions.slice(0, 3).map(action => (
            <button
              key={action.key}
              onClick={action.onClick}
              className={`flex items-center space-x-2 px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                action.variant === 'danger'
                  ? 'text-red-700 bg-red-50 hover:bg-red-100 dark:text-red-300 dark:bg-red-900/20 dark:hover:bg-red-900/30'
                  : action.variant === 'primary'
                  ? 'text-primary-700 bg-primary-50 hover:bg-primary-100 dark:text-primary-300 dark:bg-primary-900/20 dark:hover:bg-primary-900/30'
                  : 'text-gray-700 bg-gray-50 hover:bg-gray-100 dark:text-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600'
              }`}
            >
              <action.icon className="w-4 h-4" />
              <span className="hidden sm:inline">{action.label}</span>
            </button>
          ))}

          {/* More Actions Dropdown */}
          {allActions.length > 3 && (
            <div className="relative">
              <button
                onClick={() => setShowDropdown(!showDropdown)}
                className="flex items-center space-x-1 px-3 py-2 text-sm font-medium text-gray-700 bg-gray-50 hover:bg-gray-100 rounded-md dark:text-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600"
              >
                <MoreHorizontal className="w-4 h-4" />
              </button>

              {showDropdown && (
                <div className="absolute bottom-full mb-2 right-0 w-48 bg-white dark:bg-gray-800 rounded-md shadow-lg border border-gray-200 dark:border-gray-700 py-1">
                  {allActions.slice(3).map(action => (
                    <button
                      key={action.key}
                      onClick={() => {
                        action.onClick();
                        setShowDropdown(false);
                      }}
                      className="w-full flex items-center space-x-2 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                    >
                      <action.icon className="w-4 h-4" />
                      <span>{action.label}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Clear Selection */}
        <button
          onClick={onClearSelection}
          className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          title="Anulează selecția"
        >
          <X className="w-5 h-5" />
        </button>
      </div>
    </div>
  );
};

// Enhanced Table with Bulk Selection
export const BulkSelectableTable = ({
  data,
  columns,
  onRowClick,
  bulkActions,
  renderRow,
  keyField = 'id'
}) => {
  const {
    selectedItems,
    setSelectedItems,
    toggleItem,
    toggleAll,
    clearSelection,
    isAllSelected,
    isPartiallySelected,
    selectedCount,
    getSelectedData
  } = useBulkSelection(data);

  const [lastSelectedId, setLastSelectedId] = useState(null);

  const handleRowSelect = (itemId, event) => {
    if (event.shiftKey && lastSelectedId) {
      // Range selection with Shift
      const startIndex = data.findIndex(item => item[keyField] === lastSelectedId);
      const endIndex = data.findIndex(item => item[keyField] === itemId);
      
      if (startIndex !== -1 && endIndex !== -1) {
        const rangeStart = Math.min(startIndex, endIndex);
        const rangeEnd = Math.max(startIndex, endIndex);
        
        const newSelected = new Set(selectedItems);
        for (let i = rangeStart; i <= rangeEnd; i++) {
          newSelected.add(data[i][keyField]);
        }
        setSelectedItems(newSelected);
      }
    } else {
      toggleItem(itemId);
    }
    setLastSelectedId(itemId);
  };

  return (
    <>
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-800">
            <tr>
              {/* Select All Checkbox */}
              <th className="px-6 py-3 text-left">
                <div className="flex items-center">
                  <button
                    onClick={toggleAll}
                    className="text-primary-600 hover:text-primary-700"
                  >
                    {isAllSelected ? (
                      <CheckSquare className="w-5 h-5" />
                    ) : isPartiallySelected ? (
                      <div className="w-5 h-5 border-2 border-primary-600 rounded bg-primary-100 flex items-center justify-center">
                        <div className="w-2 h-2 bg-primary-600 rounded"></div>
                      </div>
                    ) : (
                      <Square className="w-5 h-5" />
                    )}
                  </button>
                </div>
              </th>

              {/* Column Headers */}
              {columns.map((column) => (
                <th
                  key={column.key}
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
                >
                  {column.header}
                </th>
              ))}
            </tr>
          </thead>

          <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
            {data.map((item, index) => {
              const isSelected = selectedItems.has(item[keyField]);
              return (
                <tr
                  key={item[keyField]}
                  className={`hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors ${
                    isSelected ? 'bg-primary-50 dark:bg-primary-900/20' : ''
                  }`}
                >
                  {/* Select Checkbox */}
                  <td className="px-6 py-4 whitespace-nowrap">
                    <button
                      onClick={(e) => handleRowSelect(item[keyField], e)}
                      className="text-primary-600 hover:text-primary-700"
                    >
                      {isSelected ? (
                        <CheckSquare className="w-5 h-5" />
                      ) : (
                        <Square className="w-5 h-5" />
                      )}
                    </button>
                  </td>

                  {/* Row Content */}
                  {renderRow ? renderRow(item, isSelected) : columns.map((column) => (
                    <td
                      key={column.key}
                      className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100 cursor-pointer"
                      onClick={() => onRowClick?.(item)}
                    >
                      {column.render ? column.render(item[column.key], item) : item[column.key]}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Bulk Actions Toolbar */}
      <BulkActionsToolbar
        selectedCount={selectedCount}
        onBulkDelete={() => bulkActions?.onBulkDelete?.(getSelectedData())}
        onBulkExport={() => bulkActions?.onBulkExport?.(getSelectedData())}
        onBulkArchive={() => bulkActions?.onBulkArchive?.(getSelectedData())}
        customActions={bulkActions?.customActions || []}
        onClearSelection={clearSelection}
      />
    </>
  );
};

export default BulkSelectableTable;